import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { JobSeniorityCombination, PositionAnswer } from "../../../../hooks";

import { Button } from "@components/base";

interface SubattributeConfig {
  id: string;
  code: string;
}

interface SectionConfig {
  id: string;
  subattributes: SubattributeConfig[];
}

const QUESTIONNAIRE_CONFIG: SectionConfig[] = [
  {
    id: "complexity_skills",
    subattributes: [
      { id: "formal_education", code: "1.1" },
      { id: "practical_knowledge", code: "1.2" },
      { id: "task_complexity", code: "1.3" },
      { id: "communication_competence", code: "1.4" },
      { id: "organizational_demand", code: "1.5" },
    ],
  },
  {
    id: "effort_strain",
    subattributes: [
      { id: "physical_demand", code: "2.1" },
      { id: "mental_emotional_demand", code: "2.2" },
    ],
  },
  {
    id: "responsibility",
    subattributes: [
      { id: "decision_impact", code: "3.1" },
      { id: "financial_responsibility", code: "3.2" },
      { id: "people_management_hs", code: "3.3" },
    ],
  },
  {
    id: "working_conditions",
    subattributes: [
      { id: "environment_risk", code: "4.1" },
      { id: "work_patterns", code: "4.2" },
    ],
  },
];

const EXAMPLE_KEYS = ["1", "4", "7", "10"] as const;

interface PositionQuestionnaireDialogProps {
  open: boolean;
  combo: JobSeniorityCombination | null;
  existingAnswers?: Record<string, PositionAnswer>;
  onClose: () => void;
  onSubmit: (answers: Record<string, PositionAnswer>) => void;
  parentTotals: Record<string, number>;
}

export const PositionQuestionnaireDialog: FC<PositionQuestionnaireDialogProps> = ({
  open,
  combo,
  existingAnswers,
  onClose,
  onSubmit,
  parentTotals,
}) => {
  const { t } = useTranslation();

  const [answers, setAnswers] = useState<Record<string, PositionAnswer>>({});

  useEffect(() => {
    if (!open) return;
    setAnswers(existingAnswers ?? {});
  }, [existingAnswers, open]);

  const handleRatingChange = (code: string, rawValue: number | string): void => {
    const normalized =
      typeof rawValue === "string"
        ? rawValue.replace(",", ".")
        : String(rawValue);
    const parsed = Number(normalized);

    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(Math.max(parsed, 0), 10);

    setAnswers((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), rating: clamped },
    }));
  };

  const handleCommentChange = (code: string, value: string): void => {
    setAnswers((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), comment: value },
    }));
  };

  const getParentMax = (parentId: string): number => parentTotals[parentId] ?? 0;

  const cappedTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    Object.entries(answers).forEach(([questionCode, answer]) => {
      const rating = Number(answer.rating);

      if (!Number.isFinite(rating) || rating <= 0) return;

      const parentId = questionCode.split(".")[0];
      const parentMax = getParentMax(parentId);
      const contribution = (rating / 10) * parentMax;

      totals[parentId] = Math.min(
        parentMax,
        (totals[parentId] ?? 0) + contribution,
      );
    });

    return totals;
  }, [answers, parentTotals]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {combo
          ? `${combo.jobTitle} — ${combo.seniority}`
          : t("paygap.questionnaire.dialogFallbackTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("paygap.questionnaire.scaleMeaning")}
        </Typography>

        {QUESTIONNAIRE_CONFIG.map((section) => {
          const parentId =
            section.subattributes[0]?.code.split(".")[0] ?? section.id;
          const parentMax = getParentMax(parentId);

          return (
            <Box key={section.id} mb={3}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                mb={1}
              >
                <Typography variant="subtitle1">
                  {t(`paygap.questionnaire.sections.${section.id}.title`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`paygap.questionnaire.sections.${section.id}.legalReference`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("paygap.gradeEstimator.max", "Max:")} {parentMax}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 1 }} />

              <Stack spacing={2}>
                {section.subattributes.map((sub) => {
                  const current = answers[sub.code]?.rating ?? 0;
                  const comment = answers[sub.code]?.comment ?? "";

                  return (
                    <Box
                      key={sub.code}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "grey.200",
                        borderRadius: 1,
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        spacing={1}
                        mb={0.5}
                      >
                        <Typography variant="subtitle2">
                          {sub.code} —{" "}
                          {t(`paygap.questionnaire.subattributes.${sub.id}.title`)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" mb={1}>
                        {t(`paygap.questionnaire.subattributes.${sub.id}.question`)}
                      </Typography>

                      <Stack direction="row" spacing={2} alignItems="center" mb={0.5}>
                        <Slider
                          value={current || 1}
                          min={1}
                          max={10}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                          onChange={(_, value): void =>
                            handleRatingChange(sub.code, value as number)
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label={t("paygap.gradeEstimator.ratingRange", "1–10")}
                          type="number"
                          size="small"
                          inputProps={{ min: 1, max: 10, inputMode: "decimal" }}
                          value={current || ""}
                          onChange={(e): void =>
                            handleRatingChange(sub.code, e.target.value)
                          }
                          sx={{ width: 96 }}
                        />
                      </Stack>

                      <Stack spacing={0.2} mb={0.5}>
                        {EXAMPLE_KEYS.map((key) => (
                          <Typography
                            key={key}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ lineHeight: 1.4 }}
                          >
                            <strong>{key}:</strong>{" "}
                            {t(
                              `paygap.questionnaire.subattributes.${sub.id}.example${key}`,
                            )}
                          </Typography>
                        ))}
                      </Stack>

                      <TextField
                        label={t("paygap.questionnaire.commentLabel")}
                        multiline
                        minRows={2}
                        fullWidth
                        value={comment}
                        onChange={(e): void =>
                          handleCommentChange(sub.code, e.target.value)
                        }
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t("paygap.questionnaire.closeLabel")}
        </Button>
        <Button variant="contained" onClick={(): void => onSubmit(answers)}>
          {t("paygap.questionnaire.applyLabel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
